<template>
  <div>
    <breadcrumb :items="breadcrumbs"></breadcrumb>
    <v-container fluid>
      <div class="d-flex mb-8">
        <h2 class="text-capitalize">{{ $t("surveyBuilder.surveyList") }}</h2>
        <v-spacer></v-spacer>
        <v-btn
          depressed
          color="primary"
          dark
          @click="showAddNewSurveyDialog = true"
        >
          {{ $t("surveyBuilder.createSurvey") }}
        </v-btn>
      </div>
      <v-card flat class="mb-4">
        <div class="text-center">
          <v-row justify="center" align="center" v-if="!surveys">
            <v-col cols="12" sm="4">
              <div class="py-16">
                <img src="/img/no-survey-list.svg" /><br />
                <strong class="red--text">{{ $t("surveyBuilder.noSurveyFound") }}</strong><br />
                <div class="py-4">
                  <small> {{ $t("surveyBuilder.createNewSurvey") }} </small>
                </div>
              </div>
            </v-col>
          </v-row>
          <div v-if="surveys" class="pa-4">
            <v-data-table
              :headers="headers"
              :items="surveys"
              :items-per-page="10"
              :footer-props="{
                'items-per-page-options': [10, 25, 50],
              }"
              :header-props="headerProps"
              hide-default-footer
              :loading="surveyLoading"
              :options.sync="tableOptions"
              :server-items-length="totalSurvey"
              loading-text="Loading Surveys..."
            >
              <template v-slot:top>
                <div class="d-flex mt-5">
                  <v-text-field
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    height="5px"
                    :placeholder="$t('search')"
                    dense
                    class="shrink"
                    v-model="search"
                    @input="searchSurvey"
                    @click:append="searchSurvey"
                  ></v-text-field>
                  <v-checkbox
                    v-if="isSuperAdmin"
                    class="ml-4 mt-1 pt-0"
                    dense
                    hide-details
                    :input-value="allOnPageSelected"
                    :indeterminate="someOnPageSelected && !allOnPageSelected"
                    :label="$t('surveyBuilder.migrateSelectAll')"
                    @change="toggleSelectAllOnPage"
                  ></v-checkbox>
                  <v-btn
                    v-if="isSuperAdmin && migrateSelection.length"
                    depressed
                    color="primary"
                    class="ml-4"
                    @click="openMigrateDialog(migrateSelection)"
                  >
                    <v-icon left small>mdi-database-export</v-icon>
                    {{ $t("surveyBuilder.migrateSelected", { count: migrateSelection.length }) }}
                  </v-btn>
                  <v-btn
                    v-if="isSuperAdmin && migrateSelection.length"
                    text
                    small
                    class="ml-1"
                    @click="migrateSelection = []"
                  >
                    {{ $t("surveyBuilder.migrateClearSelection") }}
                  </v-btn>
                  <v-spacer></v-spacer>
                  <div class="py-1 title">{{ from }} - {{ to }} {{$t('of')}} {{ totalSurvey }}</div>
                  <v-btn
                    class="mx-2"
                    fab
                    small
                    outlined
                    color="primary"
                    :disabled="tableOptions.page<=1"
                    @click="pageChange(false)"
                  >
                    <v-icon dark> mdi-chevron-left </v-icon>
                  </v-btn>
                  <v-btn class="mx-2" fab small outlined color="primary"
                    :disabled="tableOptions.page>=totalPages"
                    @click="pageChange(true)">
                    <v-icon dark> mdi-chevron-right </v-icon>
                  </v-btn>
                </div>
              </template>
              <template v-if="surveys" v-slot:body="{ items }">
                <tbody>
                  <tr
                    v-for="item in items"
                    :key="item.id"
                    style="position: relative; width: 0; height: 0"
                    @mouseover="selectItem(item)"
                    @mouseleave="selectItem(item)"
                  >
                    <td v-if="isSuperAdmin" class="col-migrate-select">
                      <v-simple-checkbox
                        :value="migrateSelectedIds.has(item.id)"
                        :ripple="false"
                        @input="toggleMigrateSelection(item)"
                      ></v-simple-checkbox>
                    </td>
                    <td class="text-left text-truncate-title">{{ item.title }}</td>
                    <td class="text-truncate-desc text-left">
                      {{ item.description || "N/A" }}
                    </td>
                    <td class="text-left col-organization">
                      <div class="text-truncate">
                        {{ item.CreatedBy?.fullName || "N/A" }}
                      </div>
                      <div class="text-truncate caption grey--text" v-if="item.CreatedBy?.email">
                        {{ item.CreatedBy.email }}
                      </div>
                      <div class="text-truncate caption grey--text" v-if="item.CreatedBy?.org?.name">
                        {{ item.CreatedBy.org.name }}
                      </div>
                    </td>
                    <td class="col-question">{{ item.questions }}</td>
                    <td class="col-response">{{ item.responses }}</td>
                    <td :class="`text-left ${(item.status ==='Inactive' && !item.isScheduled) ? 'red--text' : ''}`">
                      {{ item.isScheduled && item.status ==='Inactive' ? $t('scheduled') : item.status === 'Active' ? $t('active') : $t('inactive') }}
                    </td>
                    <div
                      v-if="item === selectedItem"
                      style="position: absolute; right: 10px; top: 5px"
                    >
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          x-small
                          fab
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          class="ml-2"
                          @click="$router.push({name:'DownloadSurveyHistory', params:{id:item.id}})"
                        >
                          <v-icon>mdi-dots-vertical</v-icon>
                        </v-btn>
                      </template>
                      <span>{{$t('Download Survey List')}}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          x-small
                          fab
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                          class="ml-2"
                          @click="showDownload = true"
                        >
                          <v-icon>mdi-download</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t("surveyBuilder.downloadSurvey") }}</span>
                    </v-tooltip>
                    <v-tooltip v-if="!item.isScheduled && item.status === 'Inactive'" bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            x-small
                            fab
                            color="primary"
                            v-bind="attrs"
                            v-on="on"
                            class="ml-2"
                            :loading="publishingSurvey"
                            @click="publishSurvey(item, 'publish')"
                          >
                            <v-icon>mdi-play-circle</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("surveyBuilder.publishSurvey") }}</span>
                      </v-tooltip>
                      <v-tooltip v-if="!item.isScheduled && item.status === 'Active'" bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            x-small
                            fab
                            color="primary"
                            v-bind="attrs"
                            v-on="on"
                            class="ml-2"
                            :loading="publishingSurvey"
                            @click="publishSurvey(item, 'end')"
                          >
                            <v-icon>mdi-stop-circle</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("surveyBuilder.endSurvey") }}</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on" class="d-inline-block">
                          <v-btn
                            x-small
                            fab
                            color="primary"
                            class="ml-2"
                            :disabled="item.isMigrated"
                            @click="showCopySurveyDialog = true"
                          >
                            <v-icon>mdi-content-copy</v-icon>
                          </v-btn>
                        </span>
                        </template>
                        <span>{{
                          item.isMigrated
                            ? $t("surveyBuilder.migrateAlreadyDone")
                            : $t("surveyBuilder.copy")
                        }}</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            v-if="item.status !== 'Completed'"
                            x-small
                            fab
                            color="primary"
                            v-bind="attrs"
                            v-on="on"
                            class="ml-2"
                            @click="editSurvey(item)"
                          >
                            <v-icon>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("edit") }}</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            x-small
                            fab
                            color="primary"
                            v-bind="attrs"
                            v-on="on"
                            @click="gotoSingleSurvey(item.id)"
                            class="ml-2"
                            ><v-icon>mdi-eye</v-icon></v-btn
                          >
                        </template>
                        <span>{{ $t("surveyBuilder.view") }}</span>
                      </v-tooltip>
                      <v-tooltip v-if="isSuperAdmin" bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on" class="d-inline-block">
                            <v-btn
                              x-small
                              fab
                              color="primary"
                              class="ml-2"
                              :disabled="item.isMigrated"
                              @click="openMigrateDialog([item])"
                            >
                              <v-icon>mdi-database-export</v-icon>
                            </v-btn>
                          </span>
                        </template>
                        <span>{{
                          item.isMigrated
                            ? $t("surveyBuilder.migrateAlreadyDone")
                            : $t("surveyBuilder.migrateSurvey")
                        }}</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            x-small
                            fab
                            v-bind="attrs"
                            v-on="on"
                            class="ml-2"
                            @click="showDeleteSurveyDialog = true"
                          >
                            <v-icon>mdi-trash-can</v-icon>
                          </v-btn>
                        </template>
                        <span>{{ $t("delete") }}</span>
                      </v-tooltip>
                    </div>
                  </tr>
                </tbody>
              </template>
              <template v-slot:footer></template>
            </v-data-table>
          </div>
        </div>
      </v-card>
    </v-container>
    <v-dialog
      v-model="showErrorModal"
      width="50%"
      @click:outside="showErrorModal = false"
      :retain-focus="false"
    >
      <v-card class="custom-card" rounded="0">
         <v-toolbar dense color="red" class="white--text">
          <v-toolbar-title class="font-weight-bold">
            {{ $t("surveyBuilder.publishSurvey") }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            class="white--text"
            @click="showErrorModal = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="black--text">
          <div class="text-center mt-5">
            {{ $t("surveyBuilder.cannotPublish") }}
          </div>
          <v-divider class="mt-5"></v-divider>
          <v-card-actions class="mt-2" style="padding-right: 0">
              <v-spacer></v-spacer>
              <v-btn
                outlined
                class="px-4 mr-2"
                right
                depressed
                @click="showErrorModal = false"
              >
                {{ $t("ok") }}
              </v-btn>
            </v-card-actions>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="showDeleteSurveyDialog"
      width="50%"
      @click:outside="showDeleteSurveyDialog = false"
      :retain-focus="false"
    >
      <v-card class="custom-card" rounded="0">
        <v-toolbar color="primary" class="white--text">
          <v-toolbar-title class="font-weight-bold">
            {{ $t("surveyBuilder.deleteSurvey") }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            class="white--text"
            @click="showDeleteSurveyDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-form ref="addNewSurvey" lazy-validation autocomplete="off">
          <v-card-text class="black--text">
            <div>
              {{ $t("surveyBuilder.deleteSurveyWarn") }}
            </div>

            <v-divider class="mt-5"></v-divider>
            <v-card-actions class="mt-10" style="padding-right: 0">
              <v-spacer></v-spacer>
              <v-btn
                outlined
                class="px-4 mr-2"
                right
                depressed
                @click="showDeleteSurveyDialog = false"
                color="primary"
              >
                {{ $t("cancel") }}
              </v-btn>

              <v-btn right class="px-4" color="primary" @click="deleteSurvey()">
                {{ $t("surveyBuilder.deleteSurvey") }}
              </v-btn>
            </v-card-actions>
          </v-card-text>
        </v-form>
      </v-card>
    </v-dialog>
    <AddNewSurveyListDialog
      v-if="showAddNewSurveyDialog"
      :dialogI="showAddNewSurveyDialog"
      @closeDialog="showAddNewSurveyDialog = false"
      @emitNewSurvey="getSurveys"
    />

    <AddNewSurveyListDialog
      v-if="showEditSurveyDialog"
      :dialogI="showEditSurveyDialog"
      :updateData="updateData"
      @closeDialog="showEditSurveyDialog = false"
      @emitNewSurvey="getSurveys"
    />
    <survey-copy-dialog :showDialog="showCopySurveyDialog" 
      @closeDialog="showCopySurveyDialog = false" @copySurvey="copySurvey"
      :selectedSurvey="selectedItem"
    />
    <survey-download :showDialog="showDownload" @closeDialog="showDownload = false" :selectedSurvey="selectedItem"/>

    <v-dialog
      v-model="showMigrateDialog"
      width="70%"
      persistent
      :retain-focus="false"
    >
      <v-card class="custom-card" rounded="0">
        <v-toolbar color="primary" class="white--text">
          <v-toolbar-title class="font-weight-bold">
            {{ migrateItems.length > 1
              ? $t("surveyBuilder.migrateSurveys")
              : $t("surveyBuilder.migrateSurvey") }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon class="white--text" :disabled="migrating" @click="closeMigrateDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="black--text pt-4 text-left">
          <div class="font-weight-bold">
            {{ migrateItems.length === 1
              ? migrateItems[0].title
              : $t("surveyBuilder.migrateSelectedCount", { count: migrateItems.length }) }}
          </div>
          <div class="caption grey--text mb-3">
            {{ $t("surveyBuilder.migrateSurveyInfo") }}
          </div>

          <v-radio-group
            v-model="migrateMode"
            :disabled="migrating"
            row
            hide-details
            class="mt-0"
          >
            <v-radio :label="$t('surveyBuilder.migrateModeDryRun')" value="dry-run"></v-radio>
            <v-radio
              :label="$t('surveyBuilder.migrateModeMigrate')"
              value="migrate"
              :disabled="allSelectedAlreadyMigrated"
            ></v-radio>
          </v-radio-group>
          <div v-if="allSelectedAlreadyMigrated" class="caption warning--text mt-1">
            {{ $t("surveyBuilder.migrateAlreadyDoneInfo") }}
          </div>

          <v-alert v-if="migrateError" type="error" dense text class="mt-4 mb-0">
            {{ migrateError }}
          </v-alert>

          <div v-if="migrateProgress.total" class="mt-4">
            <div class="d-flex align-center mb-1">
              <span class="caption font-weight-bold">
                {{ $t("surveyBuilder.migrateProgressLabel", {
                  done: migrateProgress.done,
                  total: migrateProgress.total,
                  percent: migratePercent,
                }) }}
              </span>
              <v-spacer></v-spacer>
              <span v-if="migrateProgress.current" class="caption grey--text text-truncate">
                {{ $t("surveyBuilder.migrateCurrentLabel", { title: migrateProgress.current }) }}
              </span>
            </div>
            <v-progress-linear
              :value="migratePercent"
              height="8"
              rounded
              color="primary"
            ></v-progress-linear>
          </div>

          <template v-if="migrateResults.length">
            <v-alert :type="migrateAlertType" dense text class="mt-4 mb-2">
              {{ migrateSummaryText }}
            </v-alert>
            <div class="migrate-results">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th class="text-left">{{ $t("surveyBuilder.surveyName") }}</th>
                    <th class="text-left">{{ $t("surveyBuilder.status") }}</th>
                    <th class="text-left">{{ $t("surveyBuilder.migrateDetails") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in migrateResults" :key="row.surveyId">
                    <td>{{ row.title || ("#" + row.surveyId) }}</td>
                    <td>
                      <v-chip x-small dark :color="migrateStatusColor(row.status)">
                        {{ migrateStatusLabel(row.status) }}
                      </v-chip>
                    </td>
                    <td class="caption">{{ migrateRowDetails(row) }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
            <div class="mt-2">
              <a class="caption" @click="showMigrateReport = !showMigrateReport">
                {{ showMigrateReport
                  ? $t("surveyBuilder.migrateHideFullReport")
                  : $t("surveyBuilder.migrateViewFullReport") }}
              </a>
              <pre v-if="showMigrateReport" class="caption migrate-report">{{ migrateReportJson }}</pre>
            </div>
          </template>

          <v-divider class="mt-5"></v-divider>
          <v-card-actions class="mt-2" style="padding-right: 0">
            <v-spacer></v-spacer>
            <v-btn
              v-if="migrating"
              outlined
              class="px-4 mr-2"
              color="error"
              :disabled="migrateCancelled"
              @click="migrateCancelled = true"
            >
              {{ $t("surveyBuilder.migrateStop") }}
            </v-btn>
            <v-btn
              outlined
              class="px-4 mr-2"
              color="primary"
              :disabled="migrating"
              @click="closeMigrateDialog"
            >
              {{ $t("close") }}
            </v-btn>
            <v-btn
              class="px-4"
              color="primary"
              :loading="migrating"
              :disabled="migrateMode === 'migrate' && allSelectedAlreadyMigrated"
              @click="runMigrateSurveys"
            >
              {{ $t("surveyBuilder.migrateRun") }}
            </v-btn>
          </v-card-actions>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import Breadcrumb from "@/components/core/Breadcrumb";
import loadingMixin from "@/mixins/LoadingMixin";
import AddNewSurveyListDialog from "./AddNewSurveyDialog";
import SurveyBuilderService from "@/_services/SurveyBuilderService";
import SurveyCopyDialog from './SurveyCopyDialog.vue';
import SurveyDownload from './SurveyDownload.vue';


const MIGRATE_STATUS = {
  migrated: { label: "migrateStatusMigrated", color: "success" },
  ok: { label: "migrateStatusOk", color: "success" },
  "not-eligible": { label: "migrateStatusNotEligible", color: "warning" },
  "already-migrated": { label: "migrateStatusAlreadyMigrated", color: "grey" },
  failed: { label: "migrateStatusFailed", color: "error" },
  "not-found": { label: "migrateStatusNotFound", color: "error" },
};

export default {
  components: {
    breadcrumb: Breadcrumb,
    AddNewSurveyListDialog,
    SurveyCopyDialog,
    SurveyDownload,
  },
  computed: {
    isSuperAdmin() {
      return this.$store?.getters?.isSuperAdmin === true;
    },

    headers() {
      const cols = [
        {
          text: this.$t("surveyBuilder.surveyName"),
          align: "start",
          value: "title",
          class: "black--text",
        },
        {
          text: this.$t("description"),
          value: "description",
          class: "black--text",
        },
        {
          text: this.$t("surveyBuilder.organization"),
          align: "start",
          value: "organization",
          class: "black--text",
        },
        {
          text: this.$t("surveyBuilder.questions"),
          align: "center",
          value: "questions",
          class: "black--text",
        },
        {
          text: this.$t("surveyBuilder.responses"),
          align: "center",
          value: "responses",
          class: "black--text",
        },
        {
          text: this.$t("surveyBuilder.status"),
          align: "left",
          value: "status",
          class: "black--text",
          width: 400,
        },
      ];
      if (this.isSuperAdmin) {
        cols.unshift({
          text: "",
          value: "migrateSelect",
          sortable: false,
          width: 48,
          class: "black--text",
        });
      }
      return cols;
    },
    migrateSelectedIds() {
      return new Set(this.migrateSelection.map((s) => s.id));
    },
    allOnPageSelected() {
      return this.surveys.length > 0
        && this.surveys.every((s) => this.migrateSelectedIds.has(s.id));
    },
    someOnPageSelected() {
      return this.surveys.some((s) => this.migrateSelectedIds.has(s.id));
    },

    allSelectedAlreadyMigrated() {
      return this.migrateItems.length > 0
        && this.migrateItems.every((s) => s.isMigrated === true);
    },
    migratePercent() {
      const { done, total } = this.migrateProgress;
      return total ? Math.round((done / total) * 100) : 0;
    },
    migrateTally() {
      const tally = {};
      for (const row of this.migrateResults) {
        tally[row.status] = (tally[row.status] || 0) + 1;
      }
      return tally;
    },
    migrateSummaryText() {
      const tally = this.migrateTally;
      const parts = [];
      for (const status of Object.keys(MIGRATE_STATUS)) {
        const count = tally[status];
        if (count) parts.push(`${count} ${this.migrateStatusLabel(status)}`);
      }
      const summary = parts.join(" \u00b7 ");
      if (this.migrateCancelled && !this.migrating) {
        return `${summary} \u2014 ${this.$t("surveyBuilder.migrateStopped", {
          done: this.migrateProgress.done,
          total: this.migrateProgress.total,
        })}`;
      }
      return summary;
    },
    migrateAlertType() {
      const tally = this.migrateTally;
      if (tally.failed || tally["not-found"]) return "error";
      if (tally["not-eligible"] || tally["already-migrated"]) return "warning";
      return "success";
    },
    migrateReportJson() {
      return this.migrateResults.length ? JSON.stringify(this.migrateResults, null, 2) : "";
    },
  },
  data() {
    return {
      surveys: [],
      search: '',
      from: 0,
      to:0,
      totalSurvey: 0,
      tableOptions: {},
      totalPages: 1,
      surveyLoading: false,
      showCopySurveyDialog: false,
      showDeleteSurveyDialog: false,
      showEditSurveyDialog: false,
      selectedItem: null,
      updateData: null,
      showErrorModal: false,
      publishingSurvey: false,
      headerProps: {
        'sort-icon': 'mdi-menu-up'
      },
      breadcrumbs: [
        {
          text: this.$t("dashboard"),
          disabled: false,
          to: "/",
        },
        {
          text: this.$t("surveyBuilder.surveyBuilder"),
          link: true,
          exact: true,
          disabled: true,
          to: { name: "SurveyBuilder" },
        },
      ],
      showAddNewSurveyDialog: false,
      showDownload: false,

      migrateSelection: [],
      showMigrateDialog: false,
      showMigrateReport: false,
      migrateItems: [],
      migrateMode: "dry-run",
      migrating: false,
      migrateCancelled: false,
      migrateResults: [],
      migrateProgress: { total: 0, done: 0, current: "" },
      migrateError: "",
    };
  },
  watch: {
    tableOptions: {
        async handler () {
          await this.getSurveys()
        },
        deep: true,
      },
  },

  async mounted() {
    // this.getSurveys();
  },

  methods: {
    async publishSurvey(survey, status) {
      if (!survey.surveyStatus) {
        this.showErrorModal = true;
        return;
      }
      this.publishingSurvey = true;
      await SurveyBuilderService.publishSurvey(survey.id, status)
        .then(() => {
          this.getSurveys();
        })
         .catch((err) => {
          console.err(err);
        })
        .finally(() => {
          this.publishingSurvey = false;
        })
    },
    async getSurveys() {
      this.surveyLoading = true;
      const canSort = this.tableOptions.sortBy.length === 1 && this.tableOptions.sortDesc.length === 1;
      const requestParams = {
        limit: this.tableOptions.itemsPerPage,
        page: this.tableOptions.page ?? 1,
        searchPhrase: this.search,
        orderField: canSort ? this.tableOptions.sortBy[0] : null,
        order: canSort ? this.tableOptions.sortDesc[0] ? 'ASC' : 'DESC' : null,
      };
      await SurveyBuilderService.getAllSurveys(requestParams)
        .then((res) => {
          this.surveys = res.data.rows;
          this.totalSurvey = res.data.count
          this.totalPages = Math.ceil(this.totalSurvey/this.tableOptions.itemsPerPage)
          this.from = (this.tableOptions.page-1)*this.tableOptions.itemsPerPage+1
          this.to = (this.tableOptions.page*this.tableOptions.itemsPerPage)-(this.tableOptions.itemsPerPage-res.data.rows.length)
          this.surveyLoading = false;
        })
        .catch((err) => {
          console.err(err);
        }).finally(() => {
          this.surveyLoading = false;
        })
    },
    searchSurvey() {
      // if(this.search != '') return;
      this.tableOptions.page = 1;
      this.getSurveys();
    },
    pageChange(t){
      if(t) {
        this.tableOptions.page = this.tableOptions.page+1
      } else {
        this.tableOptions.page = this.tableOptions.page-1
      }
      // this.getSurveys();
    },
    async selectItem(item) {
      this.selectedItem = item;
    },
    async deleteSurvey() {
      await SurveyBuilderService.deleteSurvey(this.selectedItem)
        .then((res) => {
          if (res.code === 200) {
            this.showDeleteSurveyDialog = false;
            this.getSurveys();
          }
        })
        .catch((err) => {
          err && this.stopLoading();
        });
    },
    editSurvey(item) {
      this.updateData = item;
      this.showEditSurveyDialog = true;
    },
    async copySurvey(survey){
      this.surveyLoading = true;
      await SurveyBuilderService.copySurvey(survey).then(async () => {
        this.surveyLoading = false;
        await this.getSurveys();
      }).catch((err) => {
        const { message } = err;
        console.err(message);
        this.surveyLoading = false;
      })
    },
    gotoSingleSurvey(id) {
      this.$router.push({ name: "SingleSurveyList", params: { id: id } });
    },
    toggleMigrateSelection(item) {
      const at = this.migrateSelection.findIndex((s) => s.id === item.id);
      if (at === -1) this.migrateSelection.push(item);
      else this.migrateSelection.splice(at, 1);
    },
    toggleSelectAllOnPage(checked) {
      const pageIds = new Set(this.surveys.map((s) => s.id));
      if (!checked) {
        this.migrateSelection = this.migrateSelection.filter((s) => !pageIds.has(s.id));
        return;
      }
      // snapshot the ids once - pushing invalidates the computed set each time
      const selected = new Set(this.migrateSelection.map((s) => s.id));
      for (const survey of this.surveys) {
        if (!selected.has(survey.id)) this.migrateSelection.push(survey);
      }
    },

    openMigrateDialog(items) {
      this.migrateItems = items.map((item) => ({ ...item }));
      this.migrateMode = "dry-run";
      this.migrateResults = [];
      this.migrateProgress = { total: 0, done: 0, current: "" };
      this.migrateCancelled = false;
      this.migrateError = "";
      this.showMigrateReport = false;
      this.showMigrateDialog = true;
    },
    closeMigrateDialog() {
      if (this.migrating) return;
      this.showMigrateDialog = false;
    },
    migrateStatusLabel(status) {
      const known = MIGRATE_STATUS[status];
      return known ? this.$t(`surveyBuilder.${known.label}`) : status;
    },
    migrateStatusColor(status) {
      return MIGRATE_STATUS[status] ? MIGRATE_STATUS[status].color : "grey";
    },
    migrateRowDetails(row) {
      if (row.reason) return row.reason;
      const c = row.counts;
      if (!c) return "";
      const parts = [];
      const add = (value, key) => {
        if (value) parts.push(`${value} ${this.$t(`surveyBuilder.${key}`)}`);
      };
      add(c.questions, "migrateColQuestions");
      add(c.options, "migrateColOptions");
      add(c.answers, "migrateColAnswers");
      add(c.usersCreated, "migrateColUsers");
      add(c.skippedRecords, "migrateColSkipped");
      return parts.length ? parts.join(" \u00b7 ") : this.$t("surveyBuilder.migrateColNothing");
    },
    async runMigrateSurveys() {
      if (!this.migrateItems.length || this.migrating) return;
      if (this.migrateMode === "migrate" && this.allSelectedAlreadyMigrated) return;

      this.migrating = true;
      this.migrateCancelled = false;
      this.migrateResults = [];
      this.migrateError = "";
      this.showMigrateReport = false;
      this.migrateProgress = { total: this.migrateItems.length, done: 0, current: "" };

      const migratedIds = new Set();
      try {

        for (const item of this.migrateItems) {
          if (this.migrateCancelled) break;
          this.migrateProgress.current = item.title || `#${item.id}`;
          try {
            const res = await SurveyBuilderService.migrateSurveys([item.id], this.migrateMode);
            const rows = (res.data && res.data.results) || [];
            this.migrateResults.push(...rows);
            for (const row of rows) {
              if (row.status === "migrated") migratedIds.add(row.surveyId);
            }
          } catch (err) {
            const code = err?.status ?? err?.data?.code;

            if (code === 403 || code === 409) {
              this.migrateError = err?.data?.message || this.$t("surveyBuilder.migrateFailed");
              break;
            }
            this.migrateResults.push({
              surveyId: item.id,
              title: item.title,
              status: "failed",
              reason: err?.data?.message || err?.statusText || this.$t("surveyBuilder.migrateFailed"),
              counts: null,
            });
          }
          this.migrateProgress.done += 1;
        }
      } finally {
        this.migrateProgress.current = "";
        this.migrating = false;
      }

      if (this.migrateMode === "migrate") {

        this.migrateItems = this.migrateItems.map((item) =>
          (migratedIds.has(item.id) ? { ...item, isMigrated: true } : item)
        );
        this.migrateSelection = this.migrateSelection.filter((s) => !migratedIds.has(s.id));
        await this.getSurveys();
      }
    },
  },
  mixins: [loadingMixin],
};
</script>
<style lang="scss" scoped>
::v-deep .v-data-table-header__icon {
  color: white !important;
}
.text-truncate-title{
  max-width: 100px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.text-truncate-desc{
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.col-organization{
  max-width: 220px;
}
.migrate-report{
  max-height: 300px;
  overflow: auto;
  background: #f5f5f5;
  padding: 8px;
  white-space: pre-wrap;
  word-break: break-word;
}
.col-migrate-select{
  width: 48px;
}
.migrate-results{
  max-height: 320px;
  overflow-y: auto;
}
.col-question{
  width: 125px;
}
.col-response {
  width: 131px;
}
</style>
